import React, { useEffect, useState } from "react";
import {
  FaEdit, FaSort, FaSortUp, FaSortDown, FaSearch, FaEye, FaToggleOn, FaToggleOff
} from "react-icons/fa";
import Sidebar from "./Sidebar";
import { useNavigate } from 'react-router-dom';
import ApprovalFormModal from "./ApprovalFormModal";
import ApprovalViewModal from "./ApprovalViewModal";
import { useScreenPermissions } from '../components/useScreenPermissions';

// ICONS (Arrow)
const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path fill="currentColor" d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
  </svg>
);
const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path fill="currentColor" d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z" />
  </svg>
);

const API_URL = "/api";

const COLORS = {
  CLOUD: "#FBFBFB",
  BABY: "#D6EAF8",
  BORDER: "rgba(226,232,240,1)",
  BABY1: "#AAD4F1",
};

const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const ApprovalMaster = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [editingFlowId, setEditingFlowId] = useState(null);
  const [viewingFlowId, setViewingFlowId] = useState(null);
  
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  const pageTheme = {
    sidebarBg: "#ffffff",
    activeBg: COLORS.BABY1,
    activeText: "#000000",
    profileBg: COLORS.BABY1,
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/approval-flows`, { headers: getAuthHeader() });
      const data = await res.json();
      setFlows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setFlows([]);
    }
    setLoading(false);
  };

  // --- NEW: Handler for Toggling Status ---
  const handleToggleStatus = async (id, currentStatus) => {
    // Determine the action based on current status
    const action = currentStatus ? 'deactivate' : 'activate';
    const endpoint = `${API_URL}/approval-flow/${action}/${id}`;

    try {
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: getAuthHeader()
      });

      if (res.ok) {
        // Refresh list to show updated status
        fetchWorkflows(); 
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating status');
    }
  };

  const handleLogout = () => navigate('/login');

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" />;
    return sortConfig.direction === "asc" ? <FaSortUp className="ml-1 w-3 h-3" /> : <FaSortDown className="ml-1 w-3 h-3" />;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
    if (!Array.isArray(flows)) return;
    const sorted = [...flows].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFlows(sorted);
  };

  const handleCreate = () => {
    setEditingFlowId(null);
    setShowForm(true);
  };

  const handleEdit = (id) => {
    setEditingFlowId(id);
    setShowForm(true);
  };

  const handleView = (id) => {
    setViewingFlowId(id);
    setShowView(true);
  };
  
  const canCreate = hasFullAccess || hasPermission('APPROVAL_FLOW', 'CanCreate');
  const canView = hasFullAccess || hasPermission('APPROVAL_FLOW', 'CanView');
  const canEdit = hasFullAccess || hasPermission('APPROVAL_FLOW', 'CanEdit');

  const filteredFlows = flows.filter((f) => {
    return searchTerm === "" || (f.FlowName && f.FlowName.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const totalPages = Math.ceil(filteredFlows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const flowsForPage = filteredFlows.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    pages.push(<button key="prev" className="p-2" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ color: currentPage === 1 ? "#9CA3AF" : "#2563eb" }}><LeftArrowIcon className="w-3 h-3" /></button>);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<button key={i} className={`px-4 py-1 font-medium ${currentPage === i ? "" : "hover:bg-gray-100"}`} onClick={() => handlePageChange(i)} style={currentPage === i ? { backgroundColor: COLORS.BABY1, color: "#0F172A" } : { color: "#2563eb" }}>{i}</button>);
    }
    pages.push(<button key="next" className="p-2" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ color: currentPage === totalPages ? "#9CA3AF" : "#2563eb" }}><RightArrowIcon className="w-3 h-3" /></button>);
    return pages;
  };

  if (permissionsLoading) return <div className="min-h-screen flex justify-center items-center" style={{ backgroundColor: COLORS.CLOUD }}><p>Loading permissions...</p></div>;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.CLOUD }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={pageTheme} onLogout={handleLogout} />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        <style>{` table.custom-table { border-collapse: separate; border-spacing: 0; width: 100%; } table.custom-table tbody tr::after { content: ""; position: absolute; left: 0; right: 0; height: 1px; background: #CBD5E1; bottom: 0; pointer-events: none; } `}</style>

        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 16 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Approval Workflow Config</h1>
              <p className="mt-1 text-base text-gray-600">Design multi-level approval flows</p>
            </div>
            {canCreate && (
               <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm" style={{ backgroundColor: COLORS.BABY1, color: "#0F172A" }}>
              <FaEdit className="w-4 h-4" /> Create New Flow
            </button>
            )}
          </div>
        </div>

        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: "#6b7280" }}>Search Workflow</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search by flow name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border text-gray-900 focus:outline-none" style={{ borderColor: COLORS.BORDER }} />
              </div>
            </div>
          </div>
        </div>

        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold text-slate-900">Workflows</h2>
            <div className="flex items-center space-x-2">{renderPaginationButtons()}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full custom-table">
              <thead className="text-xs font-bold uppercase text-gray-600" style={{ backgroundColor: COLORS.BABY }}>
                <tr>
                  <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort("FlowName")}>Flow Name {getSortIcon("FlowName")}</th>
                  <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort("DocumentType")}>Document Type {getSortIcon("DocumentType")}</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="4" className="text-center py-8">Loading...</td></tr> :
                  flowsForPage.length === 0 ? <tr><td colSpan="4" className="text-center py-8">No workflows found.</td></tr> :
                  flowsForPage.map((flow) => (
                    <tr key={flow.ApprovalFlowID} className="group hover:bg-slate-50" style={{ backgroundColor: "#ffffff" }}>
                      <td className="px-6 py-3 text-left text-sm font-semibold uppercase">{flow.FlowName}</td>
                      <td className="px-6 py-3 text-left text-sm text-gray-600 uppercase">{flow.DocumentType}</td>
                      <td className="px-6 py-3 text-left text-sm">
                         <span className={`inline-block px-3 py-1 text-xs font-bold uppercase rounded-full ${flow.IsActive ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'}`}>
                           {flow.IsActive ? 'Active' : 'Inactive'}
                         </span>
                      </td>
                      <td className="px-6 py-3 flex gap-2 justify-center">
                        {canView && (
                          <button 
                            onClick={() => handleView(flow.ApprovalFlowID)} 
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" 
                            title="View Flow Chart"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        )}
                        {canEdit && (
                          <>
                            <button 
                              onClick={() => handleEdit(flow.ApprovalFlowID)} 
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors" 
                              title="Edit Flow"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                            
                            {/* NEW: Activate/Deactivate Button
                            <button 
                              onClick={() => handleToggleStatus(flow.ApprovalFlowID, flow.IsActive)} 
                              className={`p-2 rounded-full transition-colors ${flow.IsActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} 
                              title={flow.IsActive ? 'Deactivate Flow' : 'Activate Flow'}
                            >
                              {flow.IsActive ? <FaToggleOn className="w-5 h-5" /> : <FaToggleOff className="w-5 h-5" />}
                            </button> */}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FORM MODAL */}
        {showForm && (
          <ApprovalFormModal
            open={showForm}
            onClose={() => {
                setShowForm(false);
                setEditingFlowId(null);
            }}
            apiBaseUrl={API_URL}
            fetchWorkflows={fetchWorkflows}
            editingFlowId={editingFlowId}
          />
        )}

        {/* VIEW MODAL */}
        {showView && (
          <ApprovalViewModal
            open={showView}
            onClose={() => {
                setShowView(false);
                setViewingFlowId(null);
            }}
            apiBaseUrl={API_URL}
            viewingFlowId={viewingFlowId}
          />
        )}
      </main>
    </div>
  );
};

export default ApprovalMaster;